import { SyntheticEvent, useState } from 'react';
import { useIntl } from 'react-intl';

import { Banner, FormInput, OnChangePseudoEvent } from '@calm-web/design-system';

import BackButton from '@/components/b2b/BackButton';
import { ContentWrapper } from '@/components/b2b/shared/styles';
import { BlueLine } from '@/components/b2b/Welcome/styles';
import { useAuthFormInputs } from '@/hooks/forms/useAuthFormInputs';
import { validate } from '@/utils/ui/validator';

import { formatUserIdDescriptor } from '../Eligibility';
import { CtaText } from '../Eligibility/styles';
import { SignupScreenProps } from '../shared/types';
import messages from './messages';
import { Description, MarginBanner, SubmitButton } from './styles';

const logContext = 'Request Access Flow';

const RequestAccess = ({
	onScreenChange,
	partnerDetails,
	error,
	requestAccessCall,
}: Pick<SignupScreenProps, 'onScreenChange' | 'partnerDetails' | 'error' | 'requestAccessCall'>) => {
	const { formatMessage } = useIntl();
	const [inputFields, inputActions] = useAuthFormInputs(['firstName', 'lastName', 'email'] as const);
	const [isRequesting, setIsRequesting] = useState(false);
	const isValidEmail = validate('email', inputFields.email?.value);
	const { name, user_id_descriptor, pageInfo } = partnerDetails;

	const { onChange, onBlur, onFocus } = inputActions;

	function submitAccessRequest(e: SyntheticEvent): void {
		if (isRequesting) return;
		setIsRequesting(true);
		e.preventDefault();
		e.stopPropagation();
		if (requestAccessCall) {
			requestAccessCall(
				partnerDetails.id,
				inputFields.firstName.value,
				inputFields.lastName.value,
				inputFields.email.value,
			);
		}
	}

	function handleInputChange(e: OnChangePseudoEvent): void {
		onChange(e);
	}

	const instructionsMessage = pageInfo.messages?.instructions
		? pageInfo.messages.instructions
		: messages.instructions;

	return (
		<ContentWrapper>
			<BackButton onScreenChange={onScreenChange} />
			{error?.code === 'b2b_partner_eligibility_redeem_invalid_id' && (
				<MarginBanner>
					<Banner backgroundColor="error" textColor="white">
						{formatMessage(pageInfo.messages.errorRequestAccess ?? messages.errorRequestAccess)}
					</Banner>
				</MarginBanner>
			)}
			<CtaText>{formatMessage(messages.title)}</CtaText>
			<Description>
				{formatMessage(instructionsMessage, {
					uniqueId: formatUserIdDescriptor(user_id_descriptor, formatMessage),
					partnerName: name,
				})}
			</Description>
			<BlueLine />
			<form onSubmit={submitAccessRequest}>
				<FormInput
					name="firstName"
					label="First Name"
					onChange={handleInputChange}
					onBlur={e => {
						const inputEvent = e as React.FocusEvent<HTMLInputElement>;
						onBlur(inputEvent, {
							value: inputEvent.currentTarget.value,
							context: logContext,
						});
					}}
					onFocus={onFocus}
					value={inputFields.firstName?.value}
					isValid={Boolean(inputFields.firstName?.value)}
				/>
				<FormInput
					name="lastName"
					label="Last Name"
					onChange={handleInputChange}
					onBlur={e => {
						const inputEvent = e as React.FocusEvent<HTMLInputElement>;
						onBlur(inputEvent, {
							value: inputEvent.currentTarget.value,
							context: logContext,
						});
					}}
					onFocus={onFocus}
					value={inputFields.lastName?.value}
					isValid={Boolean(inputFields.lastName?.value)}
				/>
				<FormInput
					name="email"
					label="Work Email"
					onChange={handleInputChange}
					onBlur={e => {
						const inputEvent = e as React.FocusEvent<HTMLInputElement>;
						onBlur(inputEvent, {
							value: inputEvent.currentTarget.value,
							context: logContext,
						});
					}}
					onFocus={onFocus}
					value={inputFields.email?.value}
					isValid={isValidEmail}
					error={isValidEmail ? '' : 'Please enter a valid email address.'}
				/>
				<SubmitButton
					backgroundColor="buttonLime"
					type="submit"
					isDisabled={
						!inputFields.firstName?.value ||
						!inputFields.lastName?.value ||
						!inputFields.email?.value ||
						!isValidEmail ||
						isRequesting
					}
					legacyOnClick={submitAccessRequest}
					isLoading={isRequesting}
					data-testid="b2b-partneruserid-submit"
				>
					{formatMessage(messages.title)}
				</SubmitButton>
			</form>
		</ContentWrapper>
	);
};

export default RequestAccess;
